import { useState, useEffect } from "react";

import { Card, Button, Grid, Stack } from "@mui/material";
import TextField from "@mui/material/TextField";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { SearchIcon as SearchRoundedIcon } from "components/icons";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import * as AppConstants from "AppConstants";
import { loadLeavePlansFromBackendForManager } from "app/services/manager.service";
import { checkUserHasManagerRole, getCurrentManager } from "app/services/app.state.service";
import { isEmpty } from "utils/validationUtil";
import { handleAxiosError } from "app/services/app.error.handler";
import { LeavePlanDataGridBase } from "app/components/leave-plans/LeavePlanDataGridBase";
import { LeavePlanRejectConfirmDialog } from "app/components/leave-plans/LeavePlanRejectConfirmDialog";
import { approveLeavePlan, rejectLeavePlan } from "app/services/leave-plan.service";
import { useDispatch, useSelector } from "react-redux";
import { leaveActions } from "store/leave-slice";

export const LeavePlansForManager = () => {
    const dispatch = useDispatch();
    const [manager, setManager] = useState(null);
    const [fromDate, setFromDate] = useState(useSelector((state) => state.leave.filter.startDate));
    const [toDate, setToDate] = useState(useSelector((state) => state.leave.filter.endDate));
    const leavePlansData = useSelector((state) => state.leave.rows);

    const [rejectTarget, setRejectTarget] = useState(null);

    useEffect(() => {
        if (!checkUserHasManagerRole()) return;
        if (manager) return;

        Promise.resolve(getCurrentManager()).then((mgr) => {
            if (isEmpty(mgr)) return;
            setManager(mgr);
        });
    }, [manager]);

    useEffect(() => {
        if (!manager) return;
        if (leavePlansData.length !== 0) return;
        getLeavePlansFromBackend(manager, fromDate, toDate);
    }, [manager, fromDate, toDate]);

    const searchClickHandler = async () => {
        if (!manager) return;
        getLeavePlansFromBackend(manager, fromDate, toDate);
    };

    const getLeavePlansFromBackend = async (manager, fromDate, toDate) => {
        dispatch(leaveActions.replaceFilter({ startDate: fromDate, endDate: toDate }));
        await loadLeavePlansFromBackendForManager(manager, fromDate, toDate)
            .then((response) => {
                dispatch(leaveActions.replaceData(response.data));
            })
            .catch((error) => {
                dispatch(leaveActions.replaceData([]));
                handleAxiosError(error);
            });
    };

    const handleApprove = async (row) => {
        try {
            await approveLeavePlan(row.id);
            if (manager) await getLeavePlansFromBackend(manager, fromDate, toDate);
        } catch (error) {
            handleAxiosError(error);
        }
    };

    const handleRejectClick = (row) => {
        setRejectTarget(row);
    };

    const handleRejectCancel = () => {
        setRejectTarget(null);
    };

    const handleRejectConfirm = async () => {
        const row = rejectTarget;
        setRejectTarget(null);
        if (!row) return;
        try {
            await rejectLeavePlan(row.id);
            if (manager) await getLeavePlansFromBackend(manager, fromDate, toDate);
        } catch (error) {
            handleAxiosError(error);
        }
    };

    if (!checkUserHasManagerRole()) {
        return <div>You do not have access to this functionality.</div>;
    }
    return (
        <Grid item xs={12} sm={12} md={12}>
            <Card>
                <MDBox
                    mx={2}
                    mt={-3}
                    py={1}
                    px={2}
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    coloredShadow="info"
                >
                    <MDTypography variant="h6" color="white">
                        Leave Plans
                    </MDTypography>
                </MDBox>
                <MDBox mt={3} p={2}>
                    <Stack direction="row" spacing={2}>
                        <MDBox>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="From"
                                    value={fromDate}
                                    onChange={(newFromDate) => {
                                        setFromDate(newFromDate);
                                    }}
                                    renderInput={(params) => <TextField {...params} />}
                                    inputFormat={AppConstants.DATE_FORMAT_DISPLAY}
                                />
                            </LocalizationProvider>
                        </MDBox>
                        <MDBox>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="To"
                                    value={toDate}
                                    onChange={(newToDate) => {
                                        setToDate(newToDate);
                                    }}
                                    renderInput={(params) => <TextField {...params} />}
                                    inputFormat={AppConstants.DATE_FORMAT_DISPLAY}
                                />
                            </LocalizationProvider>
                        </MDBox>
                        <MDBox>
                            <Button
                                onClick={searchClickHandler}
                                sx={{ width: 140, m: 0 }}
                                variant="contained"
                                color="success"
                                endIcon={<SearchRoundedIcon />}
                            >
                                Search
                            </Button>
                        </MDBox>
                    </Stack>
                </MDBox>

                <LeavePlanDataGridBase
                    leavePlansData={leavePlansData}
                    actions={{ onApprove: handleApprove, onReject: handleRejectClick }}
                />
            </Card>

            <LeavePlanRejectConfirmDialog
                open={Boolean(rejectTarget)}
                onCancel={handleRejectCancel}
                onConfirm={handleRejectConfirm}
            />
        </Grid>
    );
};
